import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Send, Users, Clock, CheckCircle, AlertTriangle, ArrowUpRight, Wallet, FileText, CreditCard, XCircle, Upload } from 'lucide-react';

const DOC_STATUS = {
  not_submitted: { icon: Upload, color: 'text-slate-500 bg-slate-100 border-slate-200', labelKey: 'doc_status_not_submitted' },
  pending: { icon: Clock, color: 'text-yellow-600 bg-yellow-50 border-yellow-200', labelKey: 'doc_status_pending' },
  submitted: { icon: Clock, color: 'text-yellow-600 bg-yellow-50 border-yellow-200', labelKey: 'doc_status_pending' },
  approved: { icon: CheckCircle, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', labelKey: 'doc_status_approved' },
  rejected: { icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200', labelKey: 'doc_status_rejected' },
};

function DocStatusCard({ icon: Icon, title, status, detail, t }) {
  const cfg = DOC_STATUS[status] || DOC_STATUS.not_submitted;
  const StatusIcon = cfg.icon;
  return (
    <div className="bg-card border border-border/50 rounded-2xl p-5 hover:shadow-md hover:border-accent/30 smooth-transition cursor-pointer" data-testid={`doc-status-${title}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
          <Icon className="h-5 w-5 text-accent" />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-heading text-sm font-semibold text-primary truncate">{title}</h4>
          {detail && <p className="text-xs text-muted-foreground font-mono truncate">{detail}</p>}
        </div>
      </div>
      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium ${cfg.color}`}>
        <StatusIcon className="h-3.5 w-3.5" />
        {t(cfg.labelKey)}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [recentTransfers, setRecentTransfers] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [stats, setStats] = useState(null);
  const [incomeCert, setIncomeCert] = useState(null);
  const [residenceCard, setResidenceCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [transfersRes, rateRes, statsRes, incomeCertRes, residenceCardRes] = await Promise.all([
        api.getTransfers(),
        api.getExchangeRate(),
        api.getUserStats(),
        api.getIncomeCert().catch(() => ({ data: { status: 'not_submitted' } })),
        api.getResidenceCard().catch(() => ({ data: { status: 'not_submitted' } })),
      ]);
      setRecentTransfers(transfersRes.data.slice(0, 5));
      setExchangeRate(rateRes.data);
      setStats(statsRes.data);
      setIncomeCert(incomeCertRes.data);
      setResidenceCard(residenceCardRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: t('status_pending'), className: 'bg-yellow-500/15 text-yellow-700 border-yellow-500/20' },
      processing: { label: t('status_processing'), className: 'bg-blue-500/15 text-blue-700 border-blue-500/20' },
      completed: { label: t('status_completed'), className: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/20' },
      failed: { label: t('status_failed'), className: 'bg-red-500/15 text-red-700 border-red-500/20' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.className}`}>
        {config.label}
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-2">
            {t('dashboard_greeting', { name: user?.full_name })}
          </h1>
          <p className="text-muted-foreground">
            {t('dashboard_welcome')}
          </p>
        </div>

        {/* Balance Card */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-4 sm:p-6 text-white" data-testid="balance-card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="h-5 w-5 text-slate-400" />
                <span className="text-sm text-slate-400">{t('dashboard_balance')}</span>
              </div>
              <div className="text-3xl sm:text-4xl font-bold font-mono" data-testid="user-balance">
                ¥{(user?.balance || 0).toLocaleString()}
              </div>
              {stats && (
                <div className="flex gap-4 sm:gap-6 mt-3 text-xs sm:text-sm text-slate-400">
                  <span>{t('dashboard_transfer_count')}: <span className="text-white font-medium">{stats.total_transfers}{t('count_suffix')}</span></span>
                  <span>{t('dashboard_total_sent')}: <span className="text-white font-medium">¥{(stats.total_amount_jpy || 0).toLocaleString()}</span></span>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Link to="/deposit" className="flex-1 sm:flex-none">
                <Button size="sm" variant="secondary" className="gap-2 rounded-full w-full" data-testid="deposit-button">
                  {t('dashboard_deposit_btn')}
                </Button>
              </Link>
              <Link to="/send" className="flex-1 sm:flex-none">
                <Button size="sm" className="gap-2 rounded-full bg-accent hover:bg-accent/90 text-white w-full" data-testid="send-button">
                  <Send className="h-4 w-4" />
                  {t('dashboard_send_btn')}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* KYC Alert */}
        {user?.kyc_status === 'pending' && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6" data-testid="kyc-alert">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900 mb-1">{t('kyc_required')}</h3>
                <p className="text-sm text-amber-800 mb-3">
                  {t('kyc_required_desc')}
                </p>
                <Link to="/kyc">
                  <Button size="sm" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50" data-testid="kyc-button">
                    {t('kyc_start')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Document Status Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="document-status-summary">
          {/* KYC Status */}
          <Link to="/kyc" className="block">
            <DocStatusCard
              icon={AlertTriangle}
              title={t('nav_kyc')}
              status={user?.kyc_status || 'pending'}
              t={t}
            />
          </Link>
          {/* Income Certificate Status */}
          <Link to="/income-certificate" className="block">
            <DocStatusCard
              icon={FileText}
              title={t('nav_income_cert')}
              status={incomeCert?.status || 'not_submitted'}
              detail={incomeCert?.status !== 'not_submitted' ? `¥${(incomeCert?.annual_income || 0).toLocaleString()}` : null}
              t={t}
            />
          </Link>
          {/* Residence Card Status */}
          <Link to="/residence-card" className="block">
            <DocStatusCard
              icon={CreditCard}
              title={t('nav_residence_card')}
              status={residenceCard?.status || 'not_submitted'}
              detail={residenceCard?.status !== 'not_submitted' ? residenceCard?.card_number : null}
              t={t}
            />
          </Link>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link to="/send" data-testid="quick-action-send">
            <div className="bg-card border border-border/50 rounded-2xl p-6 hover:shadow-md hover:border-accent/50 smooth-transition cursor-pointer">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                <Send className="h-6 w-6 text-accent" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-primary mb-1">{t('dashboard_send_money')}</h3>
              <p className="text-sm text-muted-foreground">{t('dashboard_send_desc')}</p>
            </div>
          </Link>

          <Link to="/recipients" data-testid="quick-action-recipients">
            <div className="bg-card border border-border/50 rounded-2xl p-6 hover:shadow-md hover:border-accent/50 smooth-transition cursor-pointer">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-accent" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-primary mb-1">{t('dashboard_recipients')}</h3>
              <p className="text-sm text-muted-foreground">{t('dashboard_recipients_desc')}</p>
            </div>
          </Link>

          <Link to="/history" data-testid="quick-action-history">
            <div className="bg-card border border-border/50 rounded-2xl p-6 hover:shadow-md hover:border-accent/50 smooth-transition cursor-pointer">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                <Clock className="h-6 w-6 text-accent" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-primary mb-1">{t('dashboard_history')}</h3>
              <p className="text-sm text-muted-foreground">{t('dashboard_history_desc')}</p>
            </div>
          </Link>
        </div>

        {/* Exchange Rate */}
        {exchangeRate && (
          <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="exchange-rate-card">
            <h3 className="font-heading text-lg font-semibold text-primary mb-4">{t('dashboard_exchange_rate')}</h3>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold font-mono text-primary">
                {exchangeRate.final_rate.toFixed(5)}
              </span>
              <span className="text-lg text-muted-foreground">BRL</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">{t('dashboard_per_jpy')}</p>
            <Link to="/exchange" className="inline-flex items-center gap-1 text-sm text-accent hover:underline mt-4">
              {t('dashboard_open_calc')}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        )}

        {/* Recent Transfers */}
        <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="recent-transfers-section">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading text-lg font-semibold text-primary">{t('dashboard_recent')}</h3>
            <Link to="/history" className="text-sm text-accent hover:underline" data-testid="view-all-link">
              {t('dashboard_view_all')}
            </Link>
          </div>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">{t('loading')}</div>
          ) : recentTransfers.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground mb-4">{t('dashboard_no_transfers')}</p>
              <Link to="/send">
                <Button size="sm" data-testid="first-transfer-button">{t('dashboard_first_transfer')}</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {recentTransfers.map((transfer) => (
                <div key={transfer.id} className="flex items-center justify-between p-4 border border-border rounded-xl hover:bg-secondary/50 smooth-transition" data-testid={`transfer-item-${transfer.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Send className="h-5 w-5 text-accent" />
                    </div>
                    <div>
                      <div className="font-medium text-primary">¥{transfer.total_jpy.toLocaleString()}</div>
                      <div className="text-sm text-muted-foreground">
                        {new Date(transfer.created_at).toLocaleDateString('ja-JP')}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(transfer.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
